import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ActivityTypeEnum,
  OfferInterface,
  UserInterface,
} from '@sport-activities/sdk';
import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, of, Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { ActivityService } from '~/core/services/activity/activity.service';
import { AgeRequirementService } from '~/core/services/activity/age-requirement.service';
import { BackLinkInterface } from '~/checkout/interfaces/back-link.interface';
import { CheckoutItem } from '~/checkout/model/checkout-item.model';
import { CheckoutService } from '~/checkout/services/checkout/checkout.service';
import { LocaleService } from '~/i18n/services/locale.service';
import { RedirectionService } from '~/core/services/redirection/redirection.service';
import { RouterHistoryService } from '~/core/services/router-history/router-history.service';
import { Subscriber } from '~/checkout/interfaces/subscriber.interface';
import { TitleService } from '~/core/services/title/title.service';
import { ParamethorNameEnum } from '~/paramethor/names.enum';
import { ParamethorService } from '~/paramethor/paramethor.service';
import { TranslationKeySubPathEnum } from '~/core/enums/translation-key-sub-path.enum';
import { UserService } from '~/auth/services/user/user.service';
import { VtmnPlayButtonSizeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-size.enum';
import { formatIsoDuration } from '~/core/helpers/duration-formatter/duration-formatter.helper';
import { getActivityDetailRouteName } from '~/attendee/helpers/activity/activity-detail-route-name.helper';
import { getRefundTimeLimit } from '~/core/helpers/refund-time-limit/refund-time-limit.helper';
import { noBlankValidator } from '~/core/validators/no-blank.validator';
import { noHtmlTagValidator } from '~/core/validators/no-html-tag/no-html-tag.validator';
import { LoaderService } from '~/core/services/loader/loader.service';
import { CartService } from '~/checkout/services/cart/cart.service';
import { ValidatorsService } from '~/core/services/validators.service';
import { BookingLimitInterface } from '~/organizer/shared/services/activity/interfaces/booking-items-group-by-user-id.interface';
import { VtmnPlayButtonShapeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-shape.enum';
import { CtaButtonTypeEnum } from '~/component/call-to-action/enums/button-type.enum';
import { MatchMediaService } from '~/core/services/match-media/match-media.service';
import {
  BookingService,
  CustomBookingStartResponseInterface,
} from '~/checkout/services/booking/booking.service';
import { OrganizationService } from '~/core/services/organization/organization.service';
import { CONNECT_ISSUER } from '~/auth/providers/connect.provider';
import { AuthService } from '~/auth/services/auth/auth.service';
import { isDefined } from '~/core/helpers/object.helper';
import { Activity } from '~/core/model/activity.model';
import { Timeslot } from '~/core/model/timeslot.model';
import { appConfig } from '~/app.config';

const ACTIVITIES_SUBSCRIPTION_LOADER_KEY = 'SUBSCRIPTION_LOADER_KEY';
const AUTHENTICATION_SUBSCRIPTION_LOADER_KEY = 'AUTHENTICATION_LOADER_KEY';
const CART_SUBSCRIPTION_LOADER_KEY = 'CART_SUBSCRIPTION_LOADER_KEY';

@Component({
  selector: 'app-subscription',
  templateUrl: './subscription.component.html',
  styleUrls: ['./subscription.component.scss'],
})
export class SubscriptionComponent
  implements OnInit, OnDestroy, AfterViewChecked
{
  private newItemSubscription: Subscription;
  private user: UserInterface;
  private readonly subscriptions = new Subscription();

  public readonly ctaButtonType = CtaButtonTypeEnum;
  public readonly vtmnPlayButtonSize = VtmnPlayButtonSizeEnum;
  public readonly vtmnPlayButtonShape = VtmnPlayButtonShapeEnum;

  public activity: Activity;
  public timeslot: Timeslot;
  public cartItem: CheckoutItem;
  public isLoading: boolean;
  public isoAuthRedirection: boolean;
  public isMobileDevice: boolean;
  public item: CheckoutItem;
  public fromCart = false;
  public requestedOffer: string;
  public requestedSku: string;
  public subscriptionForm: FormGroup;
  public newSubscriptionFormValues;
  public isEvent = false;
  public isTypeEvent = new BehaviorSubject<boolean>(false);
  public offer: OfferInterface;
  private preventReinit = false;

  constructor(
    @Inject(CONNECT_ISSUER) private readonly connectIssuer: string,
    private readonly activityService: ActivityService,
    private readonly bookingService: BookingService,
    private readonly cartService: CartService,
    private readonly checkoutService: CheckoutService,
    private readonly formBuilder: FormBuilder,
    private readonly localeService: LocaleService,
    private readonly localizeRouterService: LocalizeRouterService,
    private readonly loaderService: LoaderService,
    private readonly matchMediaService: MatchMediaService,
    private readonly redirectionService: RedirectionService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly titleService: TitleService,
    private readonly paramethorService: ParamethorService,
    private readonly translateService: TranslateService,
    private readonly userService: UserService,
    private readonly validatorsService: ValidatorsService,
    public readonly ageRequirement: AgeRequirementService,
    public readonly elementRef: ElementRef,
    public readonly routerHistory: RouterHistoryService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly organizationService: OrganizationService,
    private readonly authService: AuthService
  ) {}

  public ngOnDestroy(): void {
    this.newItemSubscription.unsubscribe();
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.subscriptions.add(
      this.loaderService.loading$.subscribe(() => {
        this.isLoading = this.loaderService.isLoading(
          ACTIVITIES_SUBSCRIPTION_LOADER_KEY
        );
      })
    );

    this.subscriptions.add(
      this.loaderService.loading$.subscribe(() => {
        this.isoAuthRedirection = this.loaderService.isLoading(
          AUTHENTICATION_SUBSCRIPTION_LOADER_KEY
        );
      })
    );

    this.route?.queryParamMap?.subscribe((params) => {
      this.loaderService.start(AUTHENTICATION_SUBSCRIPTION_LOADER_KEY);
      if (!params.has('sku') || !params.get('sku')) {
        return this.router.navigateByUrl(
          this.localizeRouterService
            .translateRoute('attendee.homepage')
            .toString()
        );
      }

      if (params.has('fromCart')) {
        this.fromCart = true;
      }

      this.requestedOffer = params.get('offer');
      this.requestedSku = params.get('sku');

      if (params.has('redirectUrl')) {
        this.checkoutService.redirectUrl = params.get('redirectUrl');
      }

      if (params.has('previousUrl')) {
        this.checkoutService.previousRedirectUrl = params.get('previousUrl');
      }

      this.activityService
        .getTimeslot(this.requestedSku)
        .pipe(
          mergeMap((timeslot) => {
            return combineLatest([
              // merge response to retrieve also activity
              of(timeslot),
              this.activityService.getActivity(timeslot.activityIdentifier),
            ]);
          })
        )
        .subscribe({
          next: ([timeslot, activity]) => {
            this.activity = activity;
            this.timeslot = timeslot;
            if (
              activity?.isBms &&
              activity?.minPriceOffer?.price === 0 &&
              !this.userService.user
            ) {
              this.redirectToLogin();
            } else {
              this.loaderService.stop(AUTHENTICATION_SUBSCRIPTION_LOADER_KEY);
            }

            let offerId = timeslot.offers[0]?.identifier;
            const timeslotOfferIdentifiers = timeslot.offers.map(
              (o) => o.identifier
            );

            // an offer has been requested AND exists on the timeslot
            if (
              this.requestedOffer &&
              timeslotOfferIdentifiers.find((i) => i === this.requestedOffer)
            ) {
              offerId = this.requestedOffer;
              this.offer = timeslot.offers.find(
                (o) => o.identifier === offerId
              );
            } else {
              this.offer = timeslot.offers[0];
            }

            this.checkoutService.addCheckoutItem({
              activity,
              timeslot,
              defaultOffer: offerId,
              phoneNumber: this.checkoutService.contactPhoneNumberData,
            });

            this.titleService.setPageTitle('seo.checkout.subscription.title', {
              title: activity?.name,
            });

            this.isEvent = activity.type === 'EVENT';
            this.isTypeEvent.next(this.isEvent);
          },
        });

      this.subscriptions.add(
        this.matchMediaService.isMobileDevice().subscribe((isMobileDevice) => {
          this.isMobileDevice = isMobileDevice;
        })
      );
    });

    this.user = this.userService.user;

    // subscribe to cart items
    this.newItemSubscription = this.checkoutService.newItem().subscribe({
      next: (checkoutItem) => {
        this.item = checkoutItem;
        if (!this.preventReinit) {
          this.setInitialValues();
        }
        this.preventReinit = false;
      },
      error: () => {
        this.loaderService.stop(ACTIVITIES_SUBSCRIPTION_LOADER_KEY);
      },
    });

    this.subscriptions.add(
      this.isTypeEvent.subscribe((isEvent) => {
        this.buildSubscriptionForm(isEvent);
        this.onInputCounterChange();
      })
    );
  }

  private redirectToLogin(): void {
    this.authService.login(this.connectIssuer, {
      from: this.router.url,
      lang: this.translateService.currentLang,
    });
  }

  private buildSubscriptionForm(isEvent: boolean): void {
    const nameValidators =
      isEvent === true
        ? []
        : [Validators.required, noBlankValidator(), noHtmlTagValidator()];
    const ageValidators =
      isEvent === true
        ? []
        : [
            Validators.required,
            Validators.max(this.ageRequirement?.tenantMajority),
          ];

    this.subscriptionForm = this.formBuilder.group({
      numberOfAdults: [0, Validators.required],
      numberOfChildren: [0, Validators.required],
      adults: new FormArray([]),
      children: new FormArray([]),
      phoneNumber: [
        this.userService.user?.phoneNumber || '',
        this.validatorsService.phoneNumber(this.localPhoneNumberFormat),
      ],
    });

    this.onInputCounterChange();

    if (this.ageRequirement?.adultsOnly(this.item?.activity)) {
      // Set the number of adults to 1
      this.formControls.numberOfAdults.setValue(
        this.cartItemsAdults?.length || 1
      );
      this.formControls.numberOfAdults.valueChanges.subscribe((value) => {
        this.onChange(value, this.adults, {
          firstname: ['', nameValidators],
          lastname: ['', nameValidators],
        });
      });

      // Fill adult
      this.setValuesFromAuth();
    } else if (this.ageRequirement?.childrenOnly(this.item?.activity)) {
      this.formControls.numberOfChildren.setValue(
        this.cartItemsChildren?.length || 1
      );
      this.formControls.numberOfChildren.valueChanges.subscribe((value) => {
        this.onChange(value, this.children, {
          firstname: ['', nameValidators],
          lastname: ['', nameValidators],
          age: ['', ageValidators],
        });
      });
    } else {
      this.formControls.numberOfAdults.setValue(
        this.cartItemsAdults?.length || 1
      );
      this.formControls.numberOfChildren.setValue(
        this.cartItemsChildren?.length || 0
      );
      this.formControls.numberOfChildren.valueChanges.subscribe((value) => {
        this.onChange(value, this.children, {
          firstname: ['', nameValidators],
          lastname: ['', nameValidators],
          age: ['', ageValidators],
        });
      });
      this.formControls.numberOfAdults.valueChanges.subscribe((value) => {
        this.onChange(value, this.adults, {
          firstname: ['', nameValidators],
          lastname: ['', nameValidators],
        });
      });
    }
  }

  public ngAfterViewChecked(): void {
    this.changeDetectorRef.detectChanges();
  }

  public get localBookingLimit(): BookingLimitInterface {
    if (
      Object.keys(this.item?.activity?.maximumAttendeesPerBooking ?? {})
        .length > 0
    ) {
      const res = this.item.activity.maximumAttendeesPerBooking;
      return {
        global: res.global,
        adults: res.adult,
        childrens: res.child,
      };
    }

    return this.paramethorService.getObject(
      ParamethorNameEnum.BookingLimit,
      {}
    );
  }

  public get globalBookingLimit(): number {
    return this.localBookingLimit.global;
  }

  public get adultsBookingLimit(): number {
    return this.localBookingLimit.adults;
  }

  public get childrensBookingLimit(): number {
    return this.localBookingLimit.childrens;
  }

  public get globalLimit(): number {
    return this.globalBookingLimit
      ? this.globalBookingLimit
      : this.item.timeslot.remainingAttendeeCapacity;
  }

  private get localPhoneNumberFormat(): string {
    return this.paramethorService.get(ParamethorNameEnum.ValidatorPhoneNumber);
  }

  public getCtaText(): string {
    return this.translateService.instant('checkout.cart.add');
  }

  private get hasAdultsInCheckout(): boolean {
    return this.item?.adults?.length > 0;
  }

  private getOrganizationName(organizationIdentifier: string): Subscription {
    return this.organizationService
      .getOrganizationById(organizationIdentifier)
      .subscribe((organization) => {
        localStorage.setItem('organizationName', organization?.name);
      });
  }

  private get hasChildrenInCheckout(): boolean {
    return this.item?.children?.length > 0;
  }

  private get hasAdultsInCart(): boolean {
    return this.cartItem?.adults?.length > 0;
  }

  private get hasChildrenInCart(): boolean {
    return this.cartItem?.children?.length > 0;
  }

  private get itemAdults(): Subscriber[] {
    return this.item?.adults;
  }

  private get itemChildren(): Subscriber[] {
    return this.item?.children;
  }

  private get cartItemsAdults(): Subscriber[] {
    return this.cartItem?.adults;
  }

  private get cartItemsChildren(): Subscriber[] {
    return this.cartItem?.children;
  }

  public get adults(): FormArray {
    return this.formControls.adults as FormArray;
  }

  public get adultsMaxCapacity(): number {
    const remainingCapacity = this.globalLimit - this.children?.length;
    const bookingLimit = this.adultsBookingLimit;
    if (!isDefined(bookingLimit)) {
      return remainingCapacity;
    }

    return Math.min(remainingCapacity, bookingLimit);
  }

  public get backLink(): BackLinkInterface {
    if (this.fromCart) {
      return {
        path: this.routerHistory.previous,
      };
    }

    let url = getActivityDetailRouteName(
      this.item?.activity.type,
      this.item?.activity.sku
    );
    let queryParams = {};

    if (
      this.item?.activity.type === ('BIRTHDAY' as ActivityTypeEnum) ||
      this.item?.activity.type === ('PRODUCT_TEST' as ActivityTypeEnum)
    ) {
      url = '/checkout.birthday-sport';

      queryParams = {
        sku: this.requestedSku,
        offer: this.requestedOffer,
      };
    }

    return {
      path:
        this.checkoutService.previousRedirectUrl ||
        this.localizeRouterService.translateRoute(url).toString(),
      queryParams,
    };
  }

  public get children(): FormArray {
    return this.formControls.children as FormArray;
  }

  public get childrenMaxCapacity(): number {
    const remainingCapacity = this.globalLimit - this.adults?.length;
    const bookingLimit = this.childrensBookingLimit;
    if (!isDefined(bookingLimit)) {
      return remainingCapacity;
    }

    return Math.min(bookingLimit, remainingCapacity);
  }

  // convenience getters for easy access to form fields
  public get formControls(): {
    [key: string]: AbstractControl;
  } {
    return this.subscriptionForm.controls;
  }

  public get getFormattedRefundTime(): string {
    return formatIsoDuration(
      getRefundTimeLimit(this.item?.activity, appConfig.refundLimit),
      this.localeService.dateLocale,
      true
    );
  }

  public get isSubmitButtonDisabled(): boolean {
    return this.subscriptionForm.invalid || this.totalSubscriber <= 0;
  }

  public get majority(): number {
    return this.ageRequirement?.tenantMajority;
  }

  public get phoneNumber(): string {
    return this.user?.phoneNumber || '';
  }

  public get phoneNumberErrors(): ValidationErrors | null {
    return this.subscriptionForm?.controls?.phoneNumber?.touched
      ? this.subscriptionForm?.get('phoneNumber')?.errors
      : null;
  }

  public get showAllowExternalPayment(): boolean {
    return (
      this.paramethorService.isTrue(ParamethorNameEnum.AllowExternalPayment) &&
      this.item?.activity?.startingPrice?.amount > 0
    );
  }

  public get totalSubscriber(): number {
    return this.adults?.length + this.children?.length;
  }

  private onInputCounterChange(): void {
    this.formControls.numberOfAdults.valueChanges.subscribe((value) => {
      if (!this.isEvent) {
        return this.onChange(value, this.adults, {
          firstname: [
            '',
            [Validators.required, noBlankValidator(), noHtmlTagValidator()],
          ],
          lastname: [
            '',
            [Validators.required, noBlankValidator(), noHtmlTagValidator()],
          ],
        });
      } else {
        return this.onChange(value, this.adults, {
          firstname: ['', []],
          lastname: ['', []],
        });
      }
    });

    this.formControls.numberOfChildren.valueChanges.subscribe((value) => {
      if (!this.isEvent) {
        return this.onChange(value, this.children, {
          firstname: [
            '',
            [Validators.required, noBlankValidator(), noHtmlTagValidator()],
          ],
          lastname: [
            '',
            [Validators.required, noBlankValidator(), noHtmlTagValidator()],
          ],
          age: [
            this.isEvent ? 1 : '',
            [
              Validators.required,
              Validators.max(this.ageRequirement.tenantMajority),
            ],
          ],
        });
      } else {
        return this.onChange(value, this.children, {
          firstname: ['', []],
          lastname: ['', []],
          age: [this.isEvent ? 1 : '', []],
        });
      }
    });
  }

  private setInitialValues(): void {
    if (this.fromCart) {
      this.getValuesFromCart();
      this.setValuesFromCart();
      return;
    }
    if (this.hasAdultsInCheckout || this.hasChildrenInCheckout) {
      this.setValuesFromCheckout();
    }
  }

  private setValuesFromAuth(): void {
    // Is Authenticated
    this.userService.isAuthenticated().subscribe((isAuthenticated) => {
      // Fill adult
      if (isAuthenticated) {
        this.adults
          .at(0)
          .get('firstname')
          .setValue(this.userService.getUser().givenName);
        this.adults
          .at(0)
          .get('lastname')
          .setValue(this.userService.getUser().familyName);
      }
    });
  }

  private getValuesFromCart() {
    return this.checkoutService.getCartContent().filter((cartItem) => {
      this.cartItem = cartItem;
    });
  }

  private setValuesFromCart() {
    if (this.hasAdultsInCart) {
      this.formControls.numberOfAdults.setValue(this.cartItemsAdults?.length);

      // Fill adults
      this.cartItemsAdults?.forEach((adult, index) => {
        const adultFirstname = !this.isEvent ? adult?.firstname : '';
        const adultLastname = !this.isEvent ? adult?.lastname : '';
        this.adults.at(index).get('firstname').setValue(adultFirstname);
        this.adults.at(index).get('lastname').setValue(adultLastname);
      });
    }

    if (this.hasChildrenInCart) {
      // Add children
      this.formControls.numberOfChildren.setValue(
        this.cartItemsChildren?.length
      );

      // Fill children
      this.cartItemsChildren.forEach((children, index) => {
        const childrenFirstname = !this.isEvent ? children?.firstname : '';
        const childrenLastname = !this.isEvent ? children?.lastname : '';
        this.children.at(index).get('firstname').setValue(childrenFirstname);
        this.children.at(index).get('lastname').setValue(childrenLastname);
        this.children.at(index).get('age').setValue(children?.age);
      });
    }
  }

  private setValuesFromCheckout(): void {
    if (this.hasAdultsInCheckout) {
      // Add adults
      this.formControls.numberOfAdults.setValue(this.itemAdults?.length);

      // Fill adults
      this.itemAdults.forEach((adult, index) => {
        this.adults.at(index).get('firstname').setValue(adult?.firstname);
        this.adults.at(index).get('lastname').setValue(adult?.lastname);
      });
    }

    if (this.hasChildrenInCheckout) {
      // Add children
      this.formControls.numberOfChildren.setValue(this.itemChildren?.length);

      // Fill children
      this.itemChildren.forEach((children, index) => {
        this.children.at(index).get('firstname').setValue(children?.firstname);
        this.children.at(index).get('lastname').setValue(children?.lastname);
        this.children.at(index).get('age').setValue(children?.age);
      });
    }
  }

  public errors(
    ageType: 'adults' | 'children',
    name: string,
    index: number
  ): ValidationErrors | null {
    const control = this[ageType].at(index).get(name);

    return control?.touched ? control.errors : null;
  }

  public isAllowed(
    allowed: 'childrenAllowed' | 'childrenOnly' | 'adultsAllowed' | 'adultsOnly'
  ): boolean {
    return this.ageRequirement[allowed](this.item?.activity);
  }

  public get majorityAge(): number {
    return this.ageRequirement?.activityMajority(this.item?.activity);
  }

  public onChange(
    subscriberCount: number,
    list: FormArray,
    validators: { [key: string]: unknown }
  ): void {
    if (list?.length < subscriberCount) {
      for (let i = list?.length; i < subscriberCount; i++) {
        list?.push(this.formBuilder.group(validators));
      }
    } else if (list?.length > subscriberCount) {
      for (let i = list?.length; i >= subscriberCount; i--) {
        list?.removeAt(i);
      }
    }
  }

  public onSubmit(): void {
    this.preventReinit = true;
    this.loaderService.start(ACTIVITIES_SUBSCRIPTION_LOADER_KEY);
    this.loaderService.start(CART_SUBSCRIPTION_LOADER_KEY);

    if (this.subscriptionForm.value.children?.length > 0) {
      const childrenArray = [];
      for (const child of this.subscriptionForm.value.children) {
        child.age = Math.trunc(Number(child.age));
        childrenArray.push(child);
      }

      this.newSubscriptionFormValues = this.subscriptionForm.value;
      this.newSubscriptionFormValues.children = childrenArray;
    }

    const { adults, children, phoneNumber } =
      this.subscriptionForm.value.children?.length === 0
        ? this.subscriptionForm.value
        : this.newSubscriptionFormValues;

    // Store the contact phone number data in the checkout service
    // in order to be patched on user at the confirmation step
    this.checkoutService.contactPhoneNumberData = {
      phoneNumber,
    };

    if (this.checkoutService.cartHasItem(this.item.timeslot)) {
      this.checkoutService.deleteCartItem(this.item.timeslot);
    }

    const checkoutItem = {
      ...this.item,
      adults,
      children,
      phoneNumber,
    };

    if (this.item?.activity?.isBms && this.item?.isFree) {
      this.getOrganizationName(this.item?.activity.organizationIdentifier);
      this.bookingService.start([checkoutItem]).subscribe({
        next: (bookingResponse: CustomBookingStartResponseInterface) => {
          this.loaderService.start(ACTIVITIES_SUBSCRIPTION_LOADER_KEY);
          const bmsTerms = [bookingResponse?.bmsTerms];
          localStorage.setItem('bookingId', bookingResponse?.bookingId);
          this.checkoutService.setCheckoutItem({
            ...this.item,
            ...{
              adults,
              children,
              bmsTerms,
            },
          });
          this.loaderService.stop(ACTIVITIES_SUBSCRIPTION_LOADER_KEY);
          this.loaderService.stop(CART_SUBSCRIPTION_LOADER_KEY);
        },
        error: (err) => {
          if (err) {
            this.loaderService.stop(CART_SUBSCRIPTION_LOADER_KEY);
            this.loaderService.stop(ACTIVITIES_SUBSCRIPTION_LOADER_KEY);
          }
        },
      });
    } else {
      this.checkoutService.setCheckoutItem(checkoutItem);
      this.loaderService.stop(CART_SUBSCRIPTION_LOADER_KEY);
      this.loaderService.stop(ACTIVITIES_SUBSCRIPTION_LOADER_KEY);
    }

    this.redirectionService.redirect(
      `/attendee.show-activity/${this.item?.activity?.sku}`
    );
    this.cartService.openCart();
  }

  public get isSharable(): boolean {
    return (
      this.adults.length >= this.adultsMaxCapacity &&
      this.timeslot.remainingAttendeeCapacity > 1
    );
  }

  public translateKey(key: string, interpolateParams?: object): string {
    let baseKey = 'checkout.subscription.';
    if (this.item?.activity.type === ('BIRTHDAY' as ActivityTypeEnum)) {
      baseKey = baseKey?.concat(TranslationKeySubPathEnum.BIRTHDAY);
    }

    return this.translateService.instant(`${baseKey}${key}`, interpolateParams);
  }
}
